import {
  Divider,
  InputAdornment,
  Slider,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { ChangeEvent, KeyboardEvent, useCallback, useEffect, useRef, useState } from 'react';

import useServerStateContext from '@/shared/hooks/useServerStateContext';
import Tracking from '@/shared/services/tracking';
import { AutoDirectorConfig } from '@/shared/types/GameStateModel';
import useAutoDirector from '@/shared/hooks/websocket/useAutoDirector';

type SliderConfigKey = 'w_dist' | 'w_angle' | 'w_end' | 'hysteresis';
type NumberConfigKey = 'hold' | 'sustain';
type ConfigKey = SliderConfigKey | NumberConfigKey;
type AutoDirectorTuning = Pick<
  AutoDirectorConfig,
  'w_dist' | 'w_angle' | 'w_end' | 'hysteresis' | 'sustain' | 'hold'
>;

type ConfigSliderProps = {
  label: string;
  name: SliderConfigKey;
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (name: SliderConfigKey, value: number) => void;
  onCommit: (name: SliderConfigKey, value: number) => void;
};

type NumberConfigInputProps = {
  label: string;
  name: NumberConfigKey;
  value: number;
  onCommit: (name: NumberConfigKey, value: number) => void;
};

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

const ConfigSlider = ({
  label,
  name,
  value,
  min,
  max,
  step,
  onChange,
  onCommit,
}: ConfigSliderProps) => {
  const formatValue = (sliderValue: number) => sliderValue.toFixed(step < 0.1 ? 2 : 1);

  return (
    <Stack spacing={0.25} sx={{ width: '100%', alignSelf: 'stretch' }}>
      <Stack
        direction="row"
        sx={{
          width: '100%',
          alignSelf: 'stretch',
          gap: 0.75,
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant="body2">{label}</Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ minWidth: 32, textAlign: 'right' }}
        >
          {formatValue(value)}
        </Typography>
      </Stack>
      <Slider
        aria-label={label}
        value={value}
        min={min}
        max={max}
        step={step}
        size="small"
        valueLabelDisplay="off"
        onChange={(_event, nextValue) => onChange(name, nextValue as number)}
        onChangeCommitted={(_event, nextValue) => onCommit(name, nextValue as number)}
        sx={{ width: '100%', boxSizing: 'border-box', py: 0.5 }}
      />
    </Stack>
  );
};

const NumberConfigInput = ({ label, name, value, onCommit }: NumberConfigInputProps) => {
  const [inputValue, setInputValue] = useState(value.toString());

  useEffect(() => {
    setInputValue(value.toString());
  }, [value]);

  const submitValue = useCallback(() => {
    const nextValue = Number(inputValue);
    const boundedValue = Number.isFinite(nextValue)
      ? clamp(Math.round(nextValue), 0, 10000)
      : value;

    setInputValue(boundedValue.toString());
    onCommit(name, boundedValue);
  }, [inputValue, name, onCommit, value]);

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.currentTarget.blur();
    }
  };

  return (
    <TextField
      fullWidth
      label={label}
      type="number"
      size="small"
      value={inputValue}
      onChange={(event: ChangeEvent<HTMLInputElement>) => setInputValue(event.target.value)}
      onBlur={submitValue}
      onKeyDown={handleKeyDown}
      slotProps={{
        input: {
          endAdornment: <InputAdornment position="end">ms</InputAdornment>,
        },
        htmlInput: {
          min: 0,
          max: 10000,
          step: 1,
          'aria-label': `${label} in milliseconds`,
        },
      }}
    />
  );
};

const AutoDirectorControls = () => {
  const { autoDirectorConfig, autoDirectorEnabled, autoDirectorDefaults } =
    useServerStateContext();
  const { disableAutoDirector, enableAutoDirector, setAutoDirectorConfig } = useAutoDirector();
  const configRef = useRef(autoDirectorConfig);
  const [draftConfig, setDraftConfig] = useState(autoDirectorConfig);

  useEffect(() => {
    configRef.current = autoDirectorConfig;
    setDraftConfig(autoDirectorConfig);
  }, [autoDirectorConfig]);

  const updateDraftConfig = useCallback((name: ConfigKey, value: number) => {
    const nextConfig = { ...configRef.current, [name]: value };
    configRef.current = nextConfig;
    setDraftConfig(nextConfig);
    return nextConfig;
  }, []);

  const sendConfig = useCallback(
    (config: AutoDirectorConfig) => {
      const tuning: AutoDirectorTuning = {
        w_dist: config.w_dist,
        w_angle: config.w_angle,
        w_end: config.w_end,
        hysteresis: config.hysteresis,
        sustain: config.sustain,
        hold: config.hold,
      };

      setAutoDirectorConfig(tuning);
      Tracking.getInstance().track('Auto-Director Config', {
        category: 'Controls',
        ...tuning,
      });
    },
    [setAutoDirectorConfig],
  );

  const handleSwitchChange = useCallback(
    (_event: ChangeEvent<HTMLInputElement>, enabled: boolean) => {
      if (enabled) {
        enableAutoDirector(autoDirectorDefaults);
      } else {
        disableAutoDirector();
      }

      Tracking.getInstance().track('Auto-Director', {
        category: 'Controls',
        enabled,
      });
    },
    [disableAutoDirector, enableAutoDirector, autoDirectorDefaults],
  );

  const handleSliderChange = useCallback(
    (name: SliderConfigKey, value: number) => {
      updateDraftConfig(name, value);
    },
    [updateDraftConfig],
  );

  const handleConfigCommit = useCallback(
    (name: ConfigKey, value: number) => {
      sendConfig(updateDraftConfig(name, value));
    },
    [sendConfig, updateDraftConfig],
  );

  return (
    <Stack
      spacing={1.25}
      sx={{
        p: 1,
        backgroundColor: '#101013',
        borderRadius: 1,
        height: '100%',
        width: '100%',
        boxSizing: 'border-box',
        overflowX: 'hidden',
        overflowY: 'auto',
      }}
    >
      <Stack
        direction="row"
        sx={{
          width: '100%',
          alignSelf: 'stretch',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography sx={{ fontWeight: 500, fontSize: '14px', lineHeight: '16px' }}>
          Auto-Director
        </Typography>
        <Stack direction="row" sx={{ ml: 1.5, alignItems: 'center' }}>
          <Switch
            checked={autoDirectorEnabled}
            onChange={handleSwitchChange}
            size="small"
            slotProps={{ input: { 'aria-label': 'Enable Auto-Director' } }}
            sx={{
              '& .MuiSwitch-track': {
                opacity: 1,
                backgroundColor: '#3b3b45',
              },
              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                opacity: 1,
                backgroundColor: 'primary.main',
              },
            }}
          />
        </Stack>
      </Stack>

      {autoDirectorEnabled && (
        <>
          <Divider />
          <Stack spacing={1.25} sx={{ width: '100%', alignSelf: 'stretch' }}>
            <Typography variant="subtitle2">Weights</Typography>
            <ConfigSlider
              label="Distance"
              name="w_dist"
              value={draftConfig.w_dist}
              min={0}
              max={1}
              step={0.01}
              onChange={handleSliderChange}
              onCommit={handleConfigCommit}
            />
            <ConfigSlider
              label="Angle"
              name="w_angle"
              value={draftConfig.w_angle}
              min={0}
              max={1}
              step={0.01}
              onChange={handleSliderChange}
              onCommit={handleConfigCommit}
            />
            <ConfigSlider
              label="End"
              name="w_end"
              value={draftConfig.w_end}
              min={0}
              max={1}
              step={0.01}
              onChange={handleSliderChange}
              onCommit={handleConfigCommit}
            />
          </Stack>

          <Divider />
          <Stack spacing={1.25} sx={{ width: '100%', alignSelf: 'stretch' }}>
            <Typography variant="subtitle2">Adjustments</Typography>
            <ConfigSlider
              label="Hysteresis"
              name="hysteresis"
              value={draftConfig.hysteresis}
              min={0}
              max={0.12}
              step={0.01}
              onChange={handleSliderChange}
              onCommit={handleConfigCommit}
            />
            <Stack spacing={1.5}>
              <NumberConfigInput
                label="Hold"
                name="hold"
                value={draftConfig.hold}
                onCommit={handleConfigCommit}
              />
              <NumberConfigInput
                label="Sustain"
                name="sustain"
                value={draftConfig.sustain}
                onCommit={handleConfigCommit}
              />
            </Stack>
          </Stack>
        </>
      )}
    </Stack>
  );
};

export default AutoDirectorControls;
